import lodash from 'lodash';
import { Ref, computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { SequenceStepInstanceAllOfStatusEnum } from '@/api/openapi';
import { CallResultApiService } from '@/api/user/resources/call_result';
import { TActionWithDisabled, TFormDirectMailSequenceStep, TFormSequenceStep, TGetStepsActions, TPartialFormSequenceStep, TStepType } from '@/components/organisms/user/general/sequence/types';
import { useNextActionTypes } from '@/composable/user/nextActionType/next-action-types';
import { DIRECT_MAIL_MANUAL_ACTION_TYPE_ID, DIRECT_MAIL_RESERVE_ACTION_TYPE_ID, getDefaultActionTypeId, getStepTypeByActionType, isDirectMailSequenceStepType } from '@/composable/user/sequence/post-put-body';
import { bitterAlert } from '@/plugins/BBitterAlert';
import { findNextStep, findMailThreadRootStep, makeSubjectForNewMail, makeSubjectForReplyInThread } from '@/services/sequence-step-service';

type TFormMode = 'create' | 'edit';
type TSaveFunc = (item: TFormSequenceStep) => void;
type TOpenStepForm = (saveFunc: TSaveFunc, step: TPartialFormSequenceStep) => void;
type TSubmitType = 'save' | 'send';

const updateFirstMailSubject = (step: TFormSequenceStep) => {
  if (!isDirectMailSequenceStepType(step) || step.replyInThread) return;
  step.subject = makeSubjectForNewMail(step);
};

// -- composable

const useSequenceStepActions = (
  getSequenceSteps: () => TFormSequenceStep[],
  getStepsActions: TGetStepsActions,
  handleUpdate?: (steps: TFormSequenceStep[]) => void,
) => {
  const internalSequenceSteps = ref<TFormSequenceStep[]>([]);
  const isMenuOpens = ref<boolean[]>([]);
  const stepsActionsWithDisabled = ref<TActionWithDisabled[][]>([]);

  const updateStepSubjectsForThread = (steps: TFormSequenceStep[]) => {
    steps.forEach((step) => {
      // NOTE: シーケンスマスタの場合のみ対象(step.status は TStepInstance の属性)、ここでUI上で書き換えて一括で保存される
      // シーケンスインスタンスはステップ単位での保存になるので、ここでは書き換えない
      if (!isDirectMailSequenceStepType(step) || !step.replyInThread || step.status) return;

      // NOTE: メールstepかつ返信の場合、スレッド先メールの件名に"Re: "を付与したものをsubjectに設定
      const prevMailStep = findMailThreadRootStep<TFormDirectMailSequenceStep>(steps, step);
      step.subject = makeSubjectForReplyInThread(prevMailStep);
    });
  };

  watch(getSequenceSteps, (newValue) => {
    const steps = lodash.cloneDeep(newValue).sort((a, b) => a.sortOrder - b.sortOrder);
    internalSequenceSteps.value = steps;
  }, { immediate: true });

  watch(internalSequenceSteps, (newValue) => {
    isMenuOpens.value = newValue.map(() => false);
    stepsActionsWithDisabled.value = getStepsActions(newValue);
    updateStepSubjectsForThread(internalSequenceSteps.value);
    if (handleUpdate !== undefined) handleUpdate(newValue);
  }, { immediate: true, deep: true });

  const closeMenu = (index: number) => {
    isMenuOpens.value = isMenuOpens.value.map((value, i) => i === index ? false : value);
  };

  return {
    internalSequenceSteps,
    isMenuOpens,
    stepsActionsWithDisabled,
    closeMenu,
  };
};

const useSequenceSelect = () => {
  const selectedSequenceStep = ref<TPartialFormSequenceStep | null>(null);
  const selectedStepType = computed(() => selectedSequenceStep.value ? getStepTypeByActionType(selectedSequenceStep.value.actionType) : null);

  return {
    selectedSequenceStep,
    selectedStepType,
  };
};

const useStepDetail = (
  internalSequenceSteps: Ref<TFormSequenceStep[]>,
) => {
  const {
    selectedSequenceStep,
    selectedStepType,
  } = useSequenceSelect();
  const { findNextActionType } = useNextActionTypes();

  const isStepDetailOpen = computed(() => selectedSequenceStep.value != null);
  const isNextActionDetailOpen = computed(() => isStepDetailOpen.value && selectedStepType.value === 'next_action');
  const isCallActionDetailOpen = computed(() => isStepDetailOpen.value && selectedStepType.value === 'call_action');
  const isDirectMailDetailOpen = computed(() => isStepDetailOpen.value && selectedStepType.value === 'direct_mail');
  const selectedNextActionType = computed(() =>
    isNextActionDetailOpen.value ? findNextActionType(selectedSequenceStep.value.actionType) : null,
  );

  const openDetail = (index: number) => {
    const step = internalSequenceSteps.value[index];
    selectedSequenceStep.value = step;
  };
  const closeStepDetail = () => {
    selectedSequenceStep.value = null;
  };

  const getCallResultByStepIndex = async (index: number) => {
    const step = internalSequenceSteps.value[index];
    if (step.callResultId == null) return null;

    const api = new CallResultApiService();
    const { data: callResult } = await api.getCallResult({
      request: { callResultId: step.callResultId },
    });
    return callResult;
  };

  return {
    isNextActionDetailOpen,
    isCallActionDetailOpen,
    isDirectMailDetailOpen,
    openDetail,
    closeStepDetail,
    getCallResultByStepIndex,
    selectedNextActionType,
    detailSelectedSequenceStep: selectedSequenceStep,
  };
};

const useStepForm = () => {
  const {
    selectedSequenceStep,
    selectedStepType,
  } = useSequenceSelect();

  const doSave = ref<TSaveFunc>();
  const isStepFormOpen = computed(() => selectedSequenceStep.value != null);
  const isNextActionFormOpen = computed(() => isStepFormOpen.value && selectedStepType.value === 'next_action');
  const isCallActionFormOpen = computed(() => isStepFormOpen.value && selectedStepType.value === 'call_action');
  const isDirectMailFormOpen = computed(() => isStepFormOpen.value && selectedStepType.value === 'direct_mail');
  const directMailSubmitType = computed(() => isDirectMailFormOpen.value && selectedSequenceStep.value?.status === 'running' ? 'send' : 'save');

  const openStepForm = (saveFunc: TSaveFunc, step: TPartialFormSequenceStep) => {
    setTimeout(() => {
      doSave.value = saveFunc;
      selectedSequenceStep.value = step;
    }, 200);
  };
  const closeStepForm = () => {
    doSave.value = undefined;
    selectedSequenceStep.value = null;
  };

  const saveStep = (item: TFormSequenceStep) => {
    doSave.value(item);
  };
  const saveStepAsDraft = (item: TFormSequenceStep) => saveStep({
    ...item,
    actionType: DIRECT_MAIL_MANUAL_ACTION_TYPE_ID,
  });
  const saveStepAsReserve = (item: TFormSequenceStep) => saveStep({
    ...item,
    actionType: DIRECT_MAIL_RESERVE_ACTION_TYPE_ID,
  });

  return {
    isNextActionFormOpen,
    isCallActionFormOpen,
    isDirectMailFormOpen,
    directMailSubmitType,
    openStepForm,
    closeStepForm,
    saveStep,
    saveStepAsDraft,
    saveStepAsReserve,
    formSelectedSequenceStep: selectedSequenceStep,
  };
};

const useStepEditing = (
  internalSequenceSteps: Ref<TFormSequenceStep[]>,
  openStepForm: TOpenStepForm,
) => {
  const openStepFormToEdit = (targetIndex: number) => {
    openStepForm(updateItem, internalSequenceSteps.value[targetIndex]);
  };

  const updateItem = (item: TFormSequenceStep) => {
    const updated = internalSequenceSteps.value.map(e => {
      if (e.sortOrder === item.sortOrder) return item;
      return e;
    });
    internalSequenceSteps.value = updated;
  };

  return {
    openStepFormToEdit,
  };
};

const useStepCreating = (
  internalSequenceSteps: Ref<TFormSequenceStep[]>,
  openStepForm: TOpenStepForm,
) => {
  const openStepFormToCreate = (stepType: TStepType, targetIndex: number) => {
    const newStep: TPartialFormSequenceStep = {
      sortOrder: targetIndex,
      priority: 'middle',
      actionType: getDefaultActionTypeId(stepType),
      deletable: true,
    };
    openStepForm(insertItem, newStep);
  };
  const insertItem = (item: TFormSequenceStep) => {
    const newSequenceSteps = lodash.cloneDeep(internalSequenceSteps.value);
    newSequenceSteps.splice(item.sortOrder, 0, item);
    newSequenceSteps.forEach((e, i) => e.sortOrder = i);
    internalSequenceSteps.value = newSequenceSteps;
  };

  return {
    openStepFormToCreate,
  };
};

const useStepDeleting = (
  internalSequenceSteps: Ref<TFormSequenceStep[]>,
) => {
  const i18n = useI18n();

  const deleteStep = async (index: number) => {
    const newSequenceSteps = lodash.cloneDeep(internalSequenceSteps.value);
    const currentItem = newSequenceSteps[index];
    if (!currentItem) return;
  
    const ok = await bitterAlert.show({
      text: i18n.t('sequence.message.confirmStepsAction', {
        number: currentItem.sortOrder + 1,
        target: i18n.t('sequence.step.title'),
        confirm: i18n.t('general.confirm.of', { action: i18n.t('general.delete.text') }),
      }),
    });
    if (!ok) { return; }
  
    setFirstMailInThread(newSequenceSteps, currentItem);
    newSequenceSteps.splice(index, 1);
    newSequenceSteps.forEach((e, i) => e.sortOrder = i);
    internalSequenceSteps.value = newSequenceSteps;
  };

  const setFirstMailInThread = (steps: TFormSequenceStep[], step: TFormSequenceStep) => {
    // スレッド元ステップが削除される時、2番目のメールをスレッド元に昇格する
    if (!isDirectMailSequenceStepType(step) || step.replyInThread) return;

    const nextMailStep = findNextStep<TFormDirectMailSequenceStep>(steps, step, 'direct_mail');
    if (!nextMailStep || !nextMailStep.replyInThread) return;

    nextMailStep.replyInThread = false;
    updateFirstMailSubject(nextMailStep);
  };

  return {
    deleteStep,
  };
};

const useStepsOrder = (
  internalSequenceSteps: Ref<TFormSequenceStep[]>,
) => {
  const changeStepsOrder = (currentIndex: number, targetIndex: number) => {
    const newSequenceSteps = lodash.cloneDeep(internalSequenceSteps.value);
    const currentItem = newSequenceSteps[currentIndex];
    const targetItem = newSequenceSteps[targetIndex];
    if (!currentItem || !targetItem) return;

    // NOTE: 該当indexを新しい要素でreplace
    const newTargetItem = { ...targetItem, sortOrder: currentItem.sortOrder };
    const newCurrentItem = { ...currentItem, sortOrder: targetItem.sortOrder };
    exchangeThreadFirstAndSecondStep(newCurrentItem, newTargetItem);
    newSequenceSteps.splice(currentIndex, 1, newTargetItem);
    newSequenceSteps.splice(targetIndex, 1, newCurrentItem);
    internalSequenceSteps.value = newSequenceSteps;
  };

  const exchangeThreadFirstAndSecondStep = (currentStep: TFormSequenceStep, targetStep: TFormSequenceStep) => {
    // スレッドの1つ目と2つ目のメールステップが入れ替わる時、スレッド元・スレッドメールの関係も入れ替える
    if (!isDirectMailSequenceStepType(currentStep) || !isDirectMailSequenceStepType(targetStep)) return;
    if (currentStep.replyInThread === targetStep.replyInThread) return;

    // 別スレッド同士の場合は、スレッド元・スレッドメールの関係は入れ替わらない（スレッド元：スレッド元のまま、スレッドメール：入れ替え対象のスレッドに所属する）
    const threadTitle1 = makeSubjectForNewMail(currentStep);
    const threadTitle2 = makeSubjectForNewMail(targetStep);
    if (threadTitle1 !== threadTitle2) return;

    currentStep.replyInThread = !currentStep.replyInThread;
    targetStep.replyInThread = !targetStep.replyInThread;
    updateFirstMailSubject(currentStep);
    updateFirstMailSubject(targetStep);
  };

  return {
    changeStepsOrder,
  };
};

const useSequenceStepMailThread = () => {
  const getAffectedStepsForThread = (steps: TFormSequenceStep[]) => {
    // メールスレッドの変更を後続ステップに反映する
    return steps.filter((step) => {
      // アクティブなメール返信、かつシーケンスマスタの場合のみ対象(step.status は TStepInstance の属性)
      if (
        !isDirectMailSequenceStepType(step)
        || !step.status
        || !step.replyInThread
        || step.status !== SequenceStepInstanceAllOfStatusEnum.Running
          && step.status !== SequenceStepInstanceAllOfStatusEnum.NotRunning
      ) return false;

      let affected = false;
      const rootStep = findMailThreadRootStep<TFormDirectMailSequenceStep>(steps, step);

      // スレッド元ステップがスキップされたら、2番目のメールをスレッド元に昇格する
      if (rootStep?.status === SequenceStepInstanceAllOfStatusEnum.Skipped) {
        step.replyInThread = false;
        step.subject = makeSubjectForNewMail(step);
        affected = true;
      } else {
        // 件名に"Re: "を付与
        const subject = makeSubjectForReplyInThread(rootStep);
        affected = step.subject !== subject;
        step.subject = subject;
      }

      return affected;
    });
  };

  return {
    getAffectedStepsForThread,
  };
};

export type {
  TFormMode,
  TSubmitType,
};
export {
  useSequenceStepActions,
  useStepDetail,
  useStepForm,
  useStepEditing,
  useStepCreating,
  useStepDeleting,
  useStepsOrder,
  useSequenceStepMailThread,
};
